import React from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to extract blog slug from URL
const getBlogSourceFromUrl = () => {
  const path = window.location.pathname;
  const referrer = document.referrer;
  
  // Check if current page is a blog post
  if (path.startsWith('/blog/') && path.length > 6) {
    return path.split('/blog/')[1].split('?')[0];
  }
  
  // Check if referrer was a blog post
  if (referrer) {
    try {
      const referrerUrl = new URL(referrer);
      if (referrerUrl.pathname.startsWith('/blog/') && referrerUrl.pathname.length > 6) {
        return referrerUrl.pathname.split('/blog/')[1].split('?')[0];
      }
    } catch (e) {
      // Invalid URL, ignore
    }
  }
  
  // Check localStorage for recent blog visit (set by BlogPost page)
  const recentBlog = localStorage.getItem('last_blog_visit');
  if (recentBlog) {
    const visitData = JSON.parse(recentBlog);
    // Only use if visited within last 30 minutes
    if (Date.now() - visitData.timestamp < 30 * 60 * 1000) {
      return visitData.slug;
    }
  }
  
  return null;
};

const LeadForm = ({ campaign = 'website', className = '', enableThankYouPage = false, thankYouSlug = 'free-trial' }) => {
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

  const onSubmit = async (data) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const blogSource = getBlogSourceFromUrl();
      
      const leadData = {
        name: data.name,
        phone: data.phone,
        campaign_source: blogSource ? `blog-${campaign}` : campaign,
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        page_url: window.location.href,
        blog_source: blogSource,
      };

      await axios.post(`${API}/leads`, leadData);
      
      // Fire Meta Pixel Lead event
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: campaign,
          content_category: 'form_submission',
        });
      }
      // Fire Google Analytics event
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          event_category: 'form',
          event_label: campaign,
        });
      }

      // Clear blog visit tracking after successful submission
      localStorage.removeItem('last_blog_visit');
      
      // Redirect to thank you page if enabled
      if (enableThankYouPage) {
        window.location.href = `/thank-you/${thankYouSlug}`;
        return;
      }
      
      toast.success('Thank you! We\'ll contact you soon.');
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`space-y-6 ${className}`}
      data-testid="lead-form"
    >
      <div>
        <input
          type="text"
          placeholder="YOUR NAME"
          {...register('name', { required: 'Name is required' })}
          className="w-full bg-cobra-gray border border-white/10 text-white placeholder:text-white/30 focus:border-cobra-red focus:ring-1 focus:ring-cobra-red rounded-sm h-14 px-4 font-manrope uppercase tracking-wide text-sm transition-colors"
          data-testid="lead-form-name-input"
        />
        {errors.name && (
          <p className="text-cobra-red text-sm mt-2 font-manrope" data-testid="lead-form-name-error">
            {errors.name.message}
          </p>
        )}
      </div>

      <div>
        <input
          type="tel"
          placeholder="YOUR PHONE"
          {...register('phone', {
            required: 'Phone is required',
            pattern: {
              value: /^[0-9+\-\s()]+$/,
              message: 'Invalid phone number',
            },
          })}
          className="w-full bg-cobra-gray border border-white/10 text-white placeholder:text-white/30 focus:border-cobra-red focus:ring-1 focus:ring-cobra-red rounded-sm h-14 px-4 font-manrope uppercase tracking-wide text-sm transition-colors"
          data-testid="lead-form-phone-input"
        />
        {errors.phone && (
          <p className="text-cobra-red text-sm mt-2 font-manrope" data-testid="lead-form-phone-error">
            {errors.phone.message}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-cobra-red text-white hover:bg-red-800 uppercase font-bold tracking-wider rounded-sm px-8 py-4 transition-all duration-300 hover:scale-105 skew-button disabled:opacity-50 disabled:cursor-not-allowed box-shadow-glow"
        data-testid="lead-form-submit-button"
      >
        {isSubmitting ? 'SUBMITTING...' : 'GET YOUR FREE TRIAL'}
      </button>
    </form>
  );
};

export default LeadForm;